import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { API_URL, errorDetail } from "@/lib/admin-api";
import { configOptions, DEVICE_LOCATION_OPTIONS } from "@/lib/config-options";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Name, location and status of a device, edited by Positive Carbon (POS2-1354).
 *
 * Status is set only here: only `active` devices count in site liveness and `inactive` ones drop
 * out of customer health, so it is a monitoring switch, not an organisation's setting. The
 * serial is the device's identity and R2 prefix (POS2-1389) and has no control.
 */

const STATUSES = ["active", "inactive", "maintenance"] as const;
const NO_LOCATION = "none";

export interface EditableDevice {
  id: string;
  name: string | null;
  location: string | null;
  status: string;
}

export function DeviceDetails({ device }: { device: EditableDevice }) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [name, setName] = useState(device.name ?? "");
  const [location, setLocation] = useState(device.location ?? NO_LOCATION);
  const [status, setStatus] = useState(device.status);

  // Only what changed is sent: the route writes the fields present, and `null` clears one.
  const changes: Record<string, string | null> = {};
  if (name !== (device.name ?? "")) changes.name = name.trim() || null;
  if (location !== (device.location ?? NO_LOCATION)) {
    changes.location = location === NO_LOCATION ? null : location;
  }
  if (status !== device.status) changes.status = status;

  const save = useMutation<unknown, Error, void>({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/devices/${device.id}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(changes),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "devices"] }),
  });

  return (
    <div className="space-y-3">
      <p className="text-xs font-medium tracking-wide text-gray-400 uppercase">Details</p>
      <div>
        <label htmlFor="admin-device-name" className="text-xs text-gray-500">
          Name
        </label>
        <Input id="admin-device-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="admin-device-location" className="text-xs text-gray-500">
            Location
          </label>
          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger id="admin-device-location" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_LOCATION}>None</SelectItem>
              {configOptions(t, DEVICE_LOCATION_OPTIONS).map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label htmlFor="admin-device-status" className="text-xs text-gray-500">
            Status
          </label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="admin-device-status" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {status === "inactive" && status !== device.status && (
        <p className="text-xs text-amber-700">
          An inactive device is left out of site liveness and customer health&apos;s quiet-device
          checks.
        </p>
      )}
      {save.isError && <p className="text-xs text-red-700">{errorDetail(save.error)}</p>}
      <Button
        onClick={() => save.mutate()}
        disabled={Object.keys(changes).length === 0 || save.isPending}
      >
        Save details
      </Button>
    </div>
  );
}
